import { useState } from "react"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api, ApiError } from "@/lib/api"
import HomeNav from "@/components/home-nav"
import HomeFooter from "@/components/home-footer"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError("")
    if (!email) {
      setError("Please enter your email address.")
      return
    }
    setIsLoading(true)
    try {
      await api.post("/auth/forgot-password", { email })
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <HomeNav />
      <div className="flex min-h-[calc(100svh-4rem)]">

        {/* Left — Form panel */}
        <div className="flex flex-1 items-center justify-center bg-white px-6 py-16 sm:px-12">
          <div className="w-full max-w-sm">

            <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />

            {submitted ? (
              <>
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-2">
                  Check your inbox
                </h1>
                <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                  If an account exists for <span className="text-[#0D0D0D]/70 font-medium">{email}</span>, you'll receive a password reset link shortly.
                </p>
                <Link
                  to="/login"
                  className="text-sm text-[#1D591B] hover:underline font-medium"
                >
                  ← Back to sign in
                </Link>
              </>
            ) : (
              <>
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-2">
                  Forgot password?
                </h1>
                <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                  Enter your email and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="email"
                      className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60"
                    >
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 bg-white"
                    />
                  </div>

                  {error && <p className="text-xs text-destructive">{error}</p>}

                  <Button
                    className="w-full rounded-none bg-primary hover:bg-foreground text-white h-12 font-semibold text-sm tracking-wide transition-colors duration-200 cursor-pointer mt-2"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending…" : "Send Reset Link"}
                  </Button>
                </form>

                <p className="text-center text-sm text-[#0D0D0D]/45 mt-8">
                  {"Remember your password? "}
                  <Link to="/login" className="text-[#1D591B] hover:underline font-medium">
                    Sign in
                  </Link>
                </p>
              </>
            )}

          </div>
        </div>

        {/* Right — Brand panel */}
        <div className="hidden lg:flex w-[48%] bg-[url('/grid.jpg')] bg-cover bg-center bg-no-repeat relative overflow-hidden">
        </div>

      </div>

      <HomeFooter />
    </>
  )
}
